<template>
  <!-- Geen dialog-semantiek: het paneel schuift naast de kaart en blokkeert die
       niet, dus role="region" met een naam past en er hoort geen focus-trap. -->
  <section
    class="marker-sidebar"
    :class="{ 'marker-sidebar-open': isVisible }"
    role="region"
    aria-label="Projectdetails"
    @keydown.esc="hide"
  >
    <div v-if="isVisible" class="sidebar-content">
      <div ref="markerContainer" tabindex="-1"></div>
    </div>
  </section>
</template>

<script lang="ts">
import { defineComponent, ref, createApp, provide, h, nextTick } from "vue";
import type { MarkerItem } from "@/types/marker";
import MarkerTemplate from "./MarkerTemplate.vue";

export default defineComponent({
  name: "SidebarMarkerDisplay",

  setup() {
    const activeMarker = ref<MarkerItem | null>(null);
    const isVisible = ref(false);
    const markerContainer = ref<HTMLElement | null>(null);

    // Element waar de focus naartoe moet als het paneel sluit. Markers worden bij
    // elke filterwijziging opnieuw opgebouwd, dus een bewaarde marker-node kan
    // ondertussen uit de DOM verdwenen zijn — vandaar de isConnected-check.
    let opener: HTMLElement | null = null;

    const show = (item: MarkerItem, config: any, vanuit: HTMLElement | null = null) => {
      opener = vanuit || (document.activeElement as HTMLElement) || null;
      activeMarker.value = item;
      isVisible.value = true;

      nextTick(() => {
        if (markerContainer.value) {
          markerContainer.value.innerHTML = '';

          const vueApp = createApp({
            setup() {
              provide('mapConfig', ref(config));
              return {};
            },
            render: () => h(MarkerTemplate, { 
              item: { 
                ...item, 
                features: item.features ? { 
                  items: item.features?.items?.map(feature => ({
                    ...feature,
                    color: feature.color || '#000000'
                  }))
                } : undefined 
              },
              onClose: hide
            })
          });
          
          vueApp.mount(markerContainer.value);
          // Focus in het paneel zetten, anders staat een toetsenbordgebruiker
          // nog bij de marker terwijl de details elders op het scherm verschijnen.
          markerContainer.value.focus();
        }
      });
    };

    const withFeatureColors = (item: MarkerItem) => ({
      ...item,
      features: item.features
        ? { items: item.features?.items?.map((f: any) => ({ ...f, color: f.color || '#000000' })) }
        : undefined,
    });

    // Toon meerdere records van één samenvallende locatie als scrollbare lijst.
    const showList = (items: MarkerItem[], config: any, vanuit: HTMLElement | null = null) => {
      opener = vanuit || (document.activeElement as HTMLElement) || null;
      activeMarker.value = null;
      isVisible.value = true;

      nextTick(() => {
        if (!markerContainer.value) return;
        markerContainer.value.innerHTML = '';

        const vueApp = createApp({
          setup() {
            provide('mapConfig', ref(config));
            return {};
          },
          render: () =>
            h('div', { class: 'marker-list' }, [
              h(
                'button',
                {
                  class: 'marker-list-close',
                  type: 'button',
                  onClick: hide,
                  // Zonder aria-label wordt de naam het maalteken zelf, dat een
                  // schermlezer als "vermenigvuldigingsteken" voorleest.
                  'aria-label': 'Lijst sluiten',
                },
                '×'
              ),
              h('h2', { class: 'marker-list-header' }, `${items.length} projecten op deze locatie`),
              ...items.map((item, idx) =>
                h('div', { class: 'marker-list-item', key: item.id ?? idx }, [
                  h(MarkerTemplate, { item: withFeatureColors(item) }),
                ])
              ),
            ]),
        });

        vueApp.mount(markerContainer.value);
        markerContainer.value.focus();
      });
    };

    const hide = () => {
      activeMarker.value = null;
      isVisible.value = false;
      if (markerContainer.value) {
        markerContainer.value.innerHTML = '';
      }
      // Focus terug naar waar de gebruiker vandaan kwam. Bestaat dat element
      // niet meer, dan blijft de focus liever op body dan op een losse node.
      if (opener && opener.isConnected) opener.focus();
      opener = null;
    };

    return {
      isVisible,
      show,
      showList,
      hide,
      markerContainer
    };
  },
});
</script>

<style scoped>
.marker-sidebar {
  position: fixed;
  right: -400px;
  top: 0;
  width: 400px;
  height: 100vh;
  background-color: var(--filter-bg-color);
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.15);
  transition: right 0.3s ease;
  z-index: 10; /* Lager dan extreem hoge waarden, maar boven de kaart */
}

.marker-sidebar-open {
  right: 0;
}

.sidebar-content {
  position: relative;
  z-index: 99999 !important;
  height: 100vh;
  overflow-y: auto;
  padding: 0.1rem;
}

@media (max-width: 768px) {
  .marker-sidebar {
    width: 100%;
    right: -100% !important;
  }
  
  .marker-sidebar-open {
    right: 0 !important;
  }
}

/* Vergroot de tekst in de sidebar */
:deep(.details) {
  font-size: 1.2em;
}

/* Optioneel: Zorg voor betere leesbaarheid */
:deep(.header) {
  font-size: 1.2em;
}

:deep(.subheader) {
  font-size: 1.0em;
}

:deep(.text strong) {
  font-size: 1.0em;
}

:deep(.text) {
  font-size: 0.8em;
}

:deep(.link) {
  font-size: 0.8em;
}

:deep(.features > div) {
  font-size: 0.8em;
}

/* Overflow-lijst (meerdere records op één locatie) */
:deep(.marker-list-header) {
  font-weight: bold;
  font-size: 1.1em;
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.15);
}

:deep(.marker-list-item) {
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  padding: 0.25rem 0;
}

:deep(.marker-list-close) {
  position: sticky;
  top: 0;
  float: right;
  border: none;
  background: transparent;
  font-size: 1.5em;
  line-height: 1;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
}
</style>
